import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

function useProfileMenuHoverOpens() {
    const [enabled, setEnabled] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
        const sync = () => setEnabled(mq.matches);
        sync();
        mq.addEventListener("change", sync);
        return () => mq.removeEventListener("change", sync);
    }, []);
    return enabled;
}

function focusableButtonsIn(container: HTMLElement): HTMLButtonElement[] {
    return Array.from(container.querySelectorAll<HTMLButtonElement>("button")).filter(
        (b) => !b.disabled && b.tabIndex !== -1,
    );
}
import { useAuth } from "src/context/AuthProvider";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
    TbLogout, TbHelpCircle, TbSend, TbAt, TbPhone, TbQuestionMark, TbClipboardCheck,
    TbShieldExclamation, TbCashBanknoteOff, TbBuildingBank, TbCopyCheck, TbCoins,
} from "react-icons/tb";
import { FaUserDoctor } from "react-icons/fa6";
import { HiOutlineCog } from "react-icons/hi";
import { TbFileEuro } from "react-icons/tb";
import { MdOutlineAccountBalance, MdSync } from "react-icons/md";
import { HiOutlineUser } from "react-icons/hi";
import { FaUserInjured } from "react-icons/fa6";
import { PiHandArrowDown } from "react-icons/pi";
import { GoHome } from "react-icons/go";
import TruncateText from "src/components/TruncateText/TruncateText";
import { isAdmin as checkAdmin, isAccounting as checkAccounting, canSeeBank as canSeeBankFor } from "src/utils/roles";
import { useApi } from "src/hooks/useApi";
import { Doctor, Invoice, Praxis, Recipient } from "src/types";
import type { FinApiStatus } from "src/hooks/useFinApi";
import { matchesInvoiceView } from "../Invoices/invoiceListViews";
import { isPostConsentBlocked } from "src/utils/invoiceSendability";
import { TrialLimits } from "./TrialLimits";

import "./header.scss";

function patientRowCount(recips: Recipient[]): number {
    return recips.reduce((sum, r) => {
        const pl = (r.patients ?? []).filter(Boolean);
        return sum + (pl.length > 1 ? pl.length : 1);
    }, 0);
}

/** Mirrors TodoCard.tsx's category set so the header hover-tooltip and the dashboard TodoCard never disagree on what counts as a todo. */
function useTodoCount(canSeeBank: boolean) {
    const { data: invoiceData } = useApi<{ invoices: Invoice[] }>("/invoices");
    const { data: recipientsData } = useApi<Recipient[]>("/recipients");
    const { data: questionsData } = useApi<{ count: number }>(
        "/dashboard/questions-count",
    );
    const { data: questionnairePendingData } = useApi<{ count: number }>(
        "/dashboard/questionnaire-pending",
    );
    const { data: settingsData } = useApi<{ requireLegalAcceptance?: boolean }>("/settings");
    const { data: bankStatus } = useApi<FinApiStatus>(canSeeBank ? "/banksapi/status" : "");

    const invoices = invoiceData?.invoices ?? [];
    const recipients = Array.isArray(recipientsData) ? recipientsData : [];
    const requireLegalAcceptance = settingsData?.requireLegalAcceptance !== false;

    const unsentInvoices = invoices.filter((inv) => inv.status === 0 && !inv.sentDate);
    // postConsentAccepted alone, regardless of postOnly — see TodoCard.tsx for why (paper
    // Einwilligung consent unlocks post even though it never unlocks email).
    const consentMissing = unsentInvoices.filter((inv) => isPostConsentBlocked(inv, requireLegalAcceptance)).length;
    const noEmail   = patientRowCount(recipients.filter((r) => !r.email));
    const noPhone   = patientRowCount(recipients.filter((r) => !r.tel || r.tel.length === 0));
    const questions = questionsData?.count ?? 0;
    const questionnairePending = questionnairePendingData?.count ?? 0;
    const overdueUnmatched = invoices.filter((inv) => matchesInvoiceView(inv, "overdue")).length;
    const overpaid = invoices.filter((inv) => {
        const vatRate = inv.vatRate ?? 0;
        const net     = (inv.amount ?? 0) + (inv.materialCosts ?? 0);
        const gross   = vatRate > 0 ? net * (1 + vatRate / 100) : net;
        const total   = gross + (inv.dunningFineAmount ?? 0);
        return (inv.paidAmount ?? 0) - total > 0.01;
    }).length;
    const unmatchedBankTx = (bankStatus?.recentTransactions ?? [])
        .filter((tx) => !tx.ignored && (tx.matchedInvoiceIds?.length ?? 0) === 0).length;
    const doublePayments = (bankStatus?.doubleMatchedInvoices ?? []).length;

    return {
        total: unsentInvoices.length + consentMissing + noEmail + noPhone + questions
            + questionnairePending + overdueUnmatched + overpaid + unmatchedBankTx + doublePayments,
        unsent: unsentInvoices.length,
        consentMissing, noEmail, noPhone, questions, questionnairePending,
        overdueUnmatched, overpaid, unmatchedBankTx, doublePayments,
    };
}

type HeaderUser = { doctor: Doctor; praxis: Praxis };

/** Todo fetches must not run when logged out — hooks run before any early return in the parent. */
const HeaderAuthed: React.FC<{ user: HeaderUser; logout: () => void }> = ({ user, logout }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);
    const profileMenuRef = useRef<HTMLDivElement>(null);
    const shellRef = useRef<HTMLDivElement>(null);
    const headerBarRef = useRef<HTMLElement>(null);
    const firstMobileNavRef = useRef<HTMLButtonElement>(null);
    const mobilePanelRef = useRef<HTMLElement>(null);
    const hamburgerRef = useRef<HTMLButtonElement>(null);
    const prevMobileMenuOpen = useRef(false);
    const profileHoverOpenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );
    const profileHoverMenu = useProfileMenuHoverOpens();

    const PROFILE_MENU_HOVER_OPEN_DELAY_MS = 320;

    const cancelProfileHoverOpenTimer = () => {
        const id = profileHoverOpenTimerRef.current;
        if (id != null) {
            clearTimeout(id);
            profileHoverOpenTimerRef.current = null;
        }
    };

    const scheduleProfileHoverOpen = () => {
        if (!profileHoverMenu) return;
        cancelProfileHoverOpenTimer();
        profileHoverOpenTimerRef.current = setTimeout(() => {
            profileHoverOpenTimerRef.current = null;
            setProfileMenuOpen(true);
        }, PROFILE_MENU_HOVER_OPEN_DELAY_MS);
    };
    const canSeeBank = canSeeBankFor(user.doctor);
    // MedicalAssistant included, Accounting excluded. Deliberately NOT gated on browser support:
    // Firefox/Safari can't do the folder sync, but the page still hosts device management for the
    // desktop agent (which works in any browser) and explains the limitation itself.
    const canSeePraxisSync = user.doctor.userRights !== 5;
    const rawTodo = useTodoCount(canSeeBank);
    const todoCount = checkAccounting(user.doctor.userRights) ? 0 : rawTodo.total;
    const todoCounts = checkAccounting(user.doctor.userRights) ? null : rawTodo;

    const PLAN_LABELS: Record<number, string> = {
        1: "BASIC",
        2: "PLUS",
        3: "PRO",
        4: "ENTERPRISE",
    };

    // Trial is only active if subscriptionStatus is "trialing" AND trialEndsAt is still in the future
    const isTrialing = user?.praxis?.subscriptionStatus === "trialing" &&
        !!user?.praxis?.trialEndsAt &&
        new Date(user.praxis.trialEndsAt) > new Date();

    const rawPlan = user?.praxis?.businessPlan ?? 1;
    const planKey = String([1, 2, 3, 4].includes(rawPlan) ? rawPlan : 1);
    const PLAN_DETAILS = useMemo(
        () => ({
            line1: t(`planDetails.${planKey}.line1`),
            line2: t(`planDetails.${planKey}.line2`),
        }),
        [t, planKey],
    );


    const openProfileMenu = () => {
        cancelProfileHoverOpenTimer();
        setProfileMenuOpen(true);
    };
    const toggleProfileMenu = () => {
        cancelProfileHoverOpenTimer();
        setProfileMenuOpen((v) => !v);
    };

    useEffect(() => () => cancelProfileHoverOpenTimer(), []);

    const syncHeaderBottom = () => {
        const shell = shellRef.current;
        const bar = headerBarRef.current;
        if (!shell || !bar) return;
        shell.style.setProperty(
            "--header-bottom",
            `${bar.getBoundingClientRect().bottom}px`,
        );
    };

    useLayoutEffect(() => {
        syncHeaderBottom();
        window.addEventListener("resize", syncHeaderBottom);
        window.addEventListener("scroll", syncHeaderBottom, true);
        return () => {
            window.removeEventListener("resize", syncHeaderBottom);
            window.removeEventListener("scroll", syncHeaderBottom, true);
        };
    }, []);

    useLayoutEffect(() => {
        if (mobileMenuOpen) syncHeaderBottom();
    }, [mobileMenuOpen]);

    useEffect(() => {
        const mq = window.matchMedia("(min-width: 901px)");
        const onChange = () => {
            if (mq.matches) setMobileMenuOpen(false);
        };
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);

    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname, location.search]);

    useEffect(() => {
        if (!mobileMenuOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setMobileMenuOpen(false);
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [mobileMenuOpen]);

    useEffect(() => {
        if (!mobileMenuOpen) return;
        const mq = window.matchMedia("(max-width: 900px)");
        if (!mq.matches) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [mobileMenuOpen]);

    useEffect(() => {
        if (!mobileMenuOpen) return;
        const mq = window.matchMedia("(max-width: 900px)");
        if (!mq.matches) return;
        const id = window.requestAnimationFrame(() => {
            firstMobileNavRef.current?.focus();
        });
        return () => window.cancelAnimationFrame(id);
    }, [mobileMenuOpen]);

    useEffect(() => {
        if (mobileMenuOpen) {
            prevMobileMenuOpen.current = true;
            return;
        }
        if (!prevMobileMenuOpen.current) return;
        prevMobileMenuOpen.current = false;
        if (window.matchMedia("(max-width: 900px)").matches) {
            hamburgerRef.current?.focus();
        }
    }, [mobileMenuOpen]);

    useEffect(() => {
        if (!mobileMenuOpen || !mobilePanelRef.current) return;
        const panel = mobilePanelRef.current;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== "Tab") return;
            const list = focusableButtonsIn(panel);
            if (list.length === 0) return;
            if (list.length === 1) {
                e.preventDefault();
                list[0].focus();
                return;
            }
            const first = list[0];
            const last = list[list.length - 1];
            if (e.shiftKey) {
                if (document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
            } else if (document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [mobileMenuOpen]);

    useEffect(() => {
        if (!profileMenuOpen) return;
        const handler = (e: MouseEvent) => {
            if (profileMenuRef.current && !profileMenuRef.current.contains(e.target as Node)) {
                setProfileMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [profileMenuOpen]);

    useEffect(() => {
        if (!profileMenuOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setProfileMenuOpen(false);
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [profileMenuOpen]);

    const ROLE_LABELS: Record<number, string> = {
        1: t("nav.assistant"),
        2: t("roles.doctor"),
        3: t("roles.admin"),
        5: t("roles.accounting"),
    };

    const role = user.doctor.userRights;
    const isAdmin = checkAdmin(role);
    const isAccounting = checkAccounting(role);
    const permSettings = user.doctor.permSettings;
    const canSeeSettings = permSettings !== undefined ? permSettings : (isAdmin || role === 2);
    const navLinks = [
        { title: t("nav.home"), path: "/main", icon: <GoHome /> },
        {
            title: t("nav.patients"),
            path: "/patients",
            icon: <FaUserInjured />,
        },
        { title: t("nav.invoices"), path: "/invoices", icon: <TbFileEuro /> },
        ...(canSeeBank ? [{ title: t("nav.bank"), path: "/bank", icon: <MdOutlineAccountBalance /> }] : []),
        ...(canSeePraxisSync ? [{ title: t("nav.praxisSoftwareSync"), path: "/praxis-software-sync", icon: <MdSync /> }] : []),
    ];

    const go = (path: string) => {
        setMobileMenuOpen(false);
        navigate(path);
    };

    const displayName = `${user.doctor.title ? `${user.doctor.title} ` : ""}${user.doctor.firstName} ${user.doctor.lastName}`;

    return (
        <div className="header-shell" ref={shellRef}>
            <header className="header" ref={headerBarRef}>
                <div
                    className="header__logo"
                    onClick={() => {
                        setMobileMenuOpen(false);
                        navigate("/main");
                    }}
                >
                    <img
                        src={user.praxis.logo || "/logoSmallWhite.jpg"}
                        alt={user.praxis.logo ? user.praxis.name : "Medinvoice"}
                        className={`header__logo-img${user.praxis.logo ? "" : " header__logo-img--fallback"}`}
                    />
                    <span className="header__logo-text">{user.praxis.name}</span>
                </div>

                <div className="header__user">
                    <div className="nav-segmented-bar">
                    {navLinks.map((link) => {
                        const isActive = location.pathname === link.path || location.pathname.startsWith(link.path + "/");
                        return (
                        <button
                            key={link.path}
                            type="button"
                            className={`nav-link${isActive ? " nav-link--active" : ""}`}
                            aria-label={link.title}
                            title={link.title}
                            onClick={() => navigate(link.path)}
                        >
                            <span className="nav-link__icon">
                                {link.path === "/main" && todoCount > 0 && location.pathname !== "/main" ? (
                                    <span className="nav-badge-wrap">
                                        {link.icon}
                                        <span className="nav-dot" aria-hidden="true" />
                                        {todoCounts && (
                                            <div className="nav-todo-tooltip">
                                                <div className="nav-todo-tooltip__title">{t("todos.title")}</div>
                                                {([
                                                    { count: todoCounts.unsent, icon: <TbSend />, color: "#f59e0b", key: "unsentInvoices" },
                                                    { count: todoCounts.consentMissing, icon: <TbShieldExclamation />, color: "#b45309", key: "consentMissing" },
                                                    { count: todoCounts.noEmail, icon: <TbAt />, color: "#6366f1", key: "noEmail" },
                                                    { count: todoCounts.noPhone, icon: <TbPhone />, color: "#14b8a6", key: "noPhone" },
                                                    { count: todoCounts.questions, icon: <TbQuestionMark />, color: "#8b5cf6", key: "unansweredQuestions" },
                                                    { count: todoCounts.questionnairePending, icon: <TbClipboardCheck />, color: "#0891b2", key: "questionnaireNoInvoice" },
                                                    { count: todoCounts.overdueUnmatched, icon: <TbCashBanknoteOff />, color: "#dc2626", key: "overdueUnmatched" },
                                                    { count: todoCounts.unmatchedBankTx, icon: <TbBuildingBank />, color: "#0e7490", key: "unmatchedBankTx" },
                                                    { count: todoCounts.doublePayments, icon: <TbCopyCheck />, color: "#b91c1c", key: "doublePayments" },
                                                    { count: todoCounts.overpaid, icon: <TbCoins />, color: "#1565c0", key: "overpaidInvoices" },
                                                ] as const).map((row) => row.count > 0 && (
                                                    <div className="nav-todo-tooltip__row" key={row.key}>
                                                        <span className="nav-todo-tooltip__icon" style={{ color: row.color }}>{row.icon}</span>
                                                        <span className="nav-todo-tooltip__label">{t(`todos.${row.key}`, { count: row.count })}</span>
                                                        <span className="nav-todo-tooltip__count" style={{ background: row.color + "18", color: row.color }}>{row.count}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </span>
                                ) : (
                                    link.icon
                                )}
                            </span>
                            <span className="nav-link__label">{link.title}</span>
                        </button>
                        );
                    })}
                    </div>
                    <div
                        ref={profileMenuRef}
                        className={`header__info${isAdmin && location.pathname === "/charges" ? " header__info--active" : ""}${profileMenuOpen ? " header__info--menu-open" : ""}`}
                        role="button"
                        tabIndex={0}
                        aria-expanded={profileMenuOpen}
                        aria-haspopup="true"
                        onMouseEnter={() => profileHoverMenu && scheduleProfileHoverOpen()}
                        onMouseLeave={() => profileHoverMenu && cancelProfileHoverOpenTimer()}
                        onClick={() => (profileHoverMenu ? openProfileMenu() : toggleProfileMenu())}
                    >
                        <button
                            type="button"
                            className="icon-button icon-button-onlytext header__avatar-btn"
                            title={t("nav.myProfile")}
                        >
                            {user.doctor.avatar ? (
                                <img
                                    src={user.doctor.avatar}
                                    alt=""
                                    className="header__avatar-img"
                                />
                            ) : (
                                <FaUserDoctor />
                            )}
                        </button>
                        <div className="header__user-info">
                            <span className="header__user-name">
                                <TruncateText text={displayName} max={30} />
                            </span>
                            <div className="header__user-meta">
                                <span className="header__user-role">
                                    {ROLE_LABELS[user.doctor.userRights] ??
                                        t("roles.user")}
                                </span>
                                {isTrialing ? (
                                    <span className="header__trial-badge">
                                        {t("header.trial", "TRIAL")}
                                    </span>
                                ) : user.praxis.businessPlan ? (
                                    <span className={`header__user-plan header__user-plan--${user.praxis.businessPlan}`}>
                                        {PLAN_LABELS[user.praxis.businessPlan]}
                                    </span>
                                ) : null}
                            </div>
                        </div>

                        <div className={`header__profile-menu${profileMenuOpen ? " header__profile-menu--open" : ""}`}>
                            <div className="header__profile-top">
                                <button
                                    type="button"
                                    className="header__profile-avatar"
                                    title={t("settings.myProfile")}
                                    onClick={(e) => { e.stopPropagation(); setProfileMenuOpen(false); navigate("/settings?section=profile"); }}
                                >
                                    {user.doctor.avatar ? (
                                        <img
                                            src={user.doctor.avatar}
                                            alt=""
                                            className="header__profile-avatar-img"
                                        />
                                    ) : (
                                        <FaUserDoctor />
                                    )}
                                </button>
                                <div className="header__profile-plan">
                                    <div className="header__profile-plan-title">
                                        <span className={`header__profile-plan-pill header__profile-plan-pill--${user.praxis.businessPlan ?? 1}`}>
                                            {PLAN_LABELS[user.praxis.businessPlan ?? 1] ?? PLAN_LABELS[1]}
                                        </span>
                                        {isTrialing && (
                                            <span className="header__trial-badge header__trial-badge--menu">
                                                {t("header.trial", "TRIAL")}
                                            </span>
                                        )}
                                    </div>
                                    {isTrialing && user.praxis.trialEndsAt && (() => {
                                        const msLeft = new Date(user.praxis.trialEndsAt).getTime() - Date.now();
                                        const daysLeft = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));
                                        return (
                                            <div className="header__trial-countdown">
                                                {daysLeft === 0
                                                    ? t("trial.daysLeft_zero")
                                                    : t(`trial.daysLeft_${daysLeft === 1 ? "one" : "other"}`, { count: daysLeft })}
                                            </div>
                                        );
                                    })()}
                                    {!(isTrialing) && (
                                        <div className="header__profile-plan-lines">
                                            <div>{PLAN_DETAILS.line1}</div>
                                            <div>{PLAN_DETAILS.line2}</div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {isTrialing && <TrialLimits isTrialing={isTrialing} />}

                            <div className="header__profile-actions">
                                {canSeeSettings && (
                                    <button
                                        type="button"
                                        className="header__profile-action"
                                        onClick={(e) => { e.stopPropagation(); setProfileMenuOpen(false); navigate("/settings"); }}
                                    >
                                        <HiOutlineCog />
                                        <span>{t("nav.settings")}</span>
                                    </button>
                                )}
                                {!isAccounting && <button
                                    type="button"
                                    className="header__profile-action header__profile-action--charges"
                                    onClick={(e) => { e.stopPropagation(); setProfileMenuOpen(false); navigate("/charges"); }}
                                >
                                    <TbFileEuro />
                                    <span>{t("nav.charges")}</span>
                                </button>}
                                <button
                                    type="button"
                                    className="header__profile-action header__profile-action--docs"
                                    onClick={(e) => { e.stopPropagation(); setProfileMenuOpen(false); navigate("/docs"); }}
                                >
                                    <TbHelpCircle />
                                    <span>{t("nav.docs")}</span>
                                </button>
                                <button
                                    type="button"
                                    className="header__profile-action header__profile-action--danger"
                                    onClick={(e) => { e.stopPropagation(); setProfileMenuOpen(false); logout(); }}
                                >
                                    <TbLogout />
                                    <span>{t("nav.logout")}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <button
                    ref={hamburgerRef}
                    type="button"
                    className={`header__hamburger${mobileMenuOpen ? " header__hamburger--open" : ""}`}
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    aria-expanded={mobileMenuOpen}
                    aria-controls="header-mobile-menu"
                    aria-label={mobileMenuOpen ? t("nav.menuClose") : t("nav.menuToggle")}
                >
                    <span />
                    <span />
                    <span />
                </button>
            </header>

            {mobileMenuOpen ? (
                <div className="header__mobile-layer" role="presentation">
                    <button
                        type="button"
                        className="header__mobile-backdrop"
                        tabIndex={-1}
                        aria-label={t("nav.menuClose")}
                        onClick={() => setMobileMenuOpen(false)}
                    />
                    <nav
                        ref={mobilePanelRef}
                        id="header-mobile-menu"
                        className="header__mobile-panel"
                        aria-label={t("nav.mainNavigation")}
                    >
                        <div className="header__mobile-nav">
                            {navLinks.map((link, i) => (
                                <button
                                    key={link.path}
                                    ref={i === 0 ? firstMobileNavRef : undefined}
                                    type="button"
                                    className={`header__mobile-nav-item${(location.pathname === link.path || location.pathname.startsWith(link.path + "/")) ? " header__mobile-nav-item--active" : ""}`}
                                    onClick={() => go(link.path)}
                                >
                                    <span className="header__mobile-nav-icon" aria-hidden>
                                        {link.path === "/main" && todoCount > 0 && location.pathname !== "/main" ? (
                                            <span className="nav-badge-wrap">
                                                {link.icon}
                                                <span className="nav-dot" />
                                            </span>
                                        ) : (
                                            link.icon
                                        )}
                                    </span>
                                    <span className="header__mobile-nav-label">{link.title}</span>
                                </button>
                            ))}
                        </div>

                        <div className="header__mobile-account">
                            <div className="header__mobile-account-card">
                                <div className="header__mobile-account-avatar" aria-hidden>
                                    {user.doctor.avatar ? (
                                        <img src={user.doctor.avatar} alt="" className="header__avatar-img" />
                                    ) : (
                                        <FaUserDoctor />
                                    )}
                                </div>
                                <div className="header__mobile-account-text">
                                    <div className="header__mobile-account-name">
                                        <TruncateText text={displayName} max={36} />
                                    </div>
                                    <div className="header__mobile-account-meta">
                                        <span className="header__user-role">
                                            {ROLE_LABELS[user.doctor.userRights] ?? t("roles.user")}
                                        </span>
                                        {isTrialing ? (
                                            <span className="header__trial-badge">
                                                {t("header.trial", "TRIAL")}
                                            </span>
                                        ) : user.praxis.businessPlan ? (
                                            <span className={`header__user-plan header__user-plan--${user.praxis.businessPlan}`}>
                                                {PLAN_LABELS[user.praxis.businessPlan]}
                                            </span>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                            <div className="header__mobile-account-actions">
                                {canSeeSettings && (
                                    <button
                                        type="button"
                                        className="header__mobile-account-btn"
                                        onClick={() => go("/settings")}
                                    >
                                        <HiOutlineCog />
                                        <span>{t("nav.settings")}</span>
                                    </button>
                                )}
                                {!isAccounting && <button
                                    type="button"
                                    className={`header__mobile-account-btn header__mobile-account-btn--charges${location.pathname === "/charges" ? " header__mobile-account-btn--active" : ""}`}
                                    onClick={() => go("/charges")}
                                >
                                    <TbFileEuro />
                                    <span>{t("nav.charges")}</span>
                                </button>}
                                <button
                                    type="button"
                                    className="header__mobile-account-btn header__mobile-account-btn--docs"
                                    onClick={() => { setMobileMenuOpen(false); navigate("/docs"); }}
                                >
                                    <TbHelpCircle />
                                    <span>{t("nav.docs")}</span>
                                </button>
                                <button
                                    type="button"
                                    className="header__mobile-account-btn header__mobile-account-btn--danger"
                                    onClick={() => {
                                        setMobileMenuOpen(false);
                                        logout();
                                    }}
                                >
                                    <TbLogout />
                                    <span>{t("nav.logout")}</span>
                                </button>
                            </div>
                        </div>
                    </nav>
                </div>
            ) : null}

        </div>
    );
};

const Header: React.FC = () => {
    const { user, logout } = useAuth();
    if (!user) return null;
    return <HeaderAuthed user={user} logout={logout} />;
};

export default Header;
